'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlay, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { colorPalette } from 'src/utils/constants/colorPalette.constants';
import { Typography } from 'src/components/PulseUI';
import { hexToRgb } from 'src/utils/color/hexToRgb';

type BucketType = 'no_status' | 'todo' | 'in_progress' | 'done';

interface GridStatusFilterMenuHeaderProps {
  bucket: string;
}

const GridStatusFilterMenuHeader = ({ bucket }: GridStatusFilterMenuHeaderProps) => {
  const bucketMap: Record<BucketType, {
    label: string;
    color: string;
    icon: React.ReactNode;
    borderStyle: string;
  }> = {
    no_status: {
      label: 'No Status',
      color: colorPalette?.grey?.main,
      icon: null,
      borderStyle: 'none',
    },
    todo: {
      label: 'To Do',
      color: colorPalette?.grey?.main,
      icon: null,
      borderStyle: 'dotted',
    },
    in_progress: {
      label: 'In Progress',
      color: colorPalette?.blue?.main,
      icon: (
        <Icon
          icon={faPlay}
          style={{ color: colorPalette?.blue?.main }}
          aria-hidden="true"
        />
      ),
      borderStyle: 'solid',
    },
    done: {
      label: 'Done',
      color: colorPalette?.green?.main,
      icon: (
        <Icon
          icon={faCheck}
          style={{ color: colorPalette?.green?.main }}
          aria-hidden="true"
        />
      ),
      borderStyle: 'solid',
    },
  };

  const config = bucketMap[bucket as BucketType];
  const isNoStatus = bucket === 'no_status';

  if (!config) return null;

  return (
    <div
      className="flex items-center justify-center gap-2"
      data-testid={`status-filter-header-${bucket}`}
    >
      <div
        className="w-[1.1rem] h-[1.1rem] p-1 rounded-full flex items-center justify-center"
        style={{
          border: `2px ${config.borderStyle} ${config.color}`,
          backgroundColor: isNoStatus
            ? hexToRgb(config.color, 0.4)
            : 'transparent',
        }}
        aria-hidden="true"
      >
        {config.icon}
      </div>
      <Typography
        variant="subtitle2"
        bold
        sx={{ color: config.color }}
      >
        {config.label}
      </Typography>
    </div>
  );
};

export default GridStatusFilterMenuHeader;
